import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GRANTS } from '../../../auth/grants'
import { useGrants } from '../../../auth/useCurrentUser'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import { useToast } from '../../../components/ui/ToastProvider'
import { useProxyMode } from '../../proxy/useProxyMode'
import { CLOSE_MAX_ITEMS, type MeetingManagementTask } from '../types'
import { useCloseTasks } from './useMeetingManagement'

/**
 * Onay Bekleniyor görevleri için <b>Kapat / Yeniden Aç</b> kararının ekran mantığı — seçim, buton
 * görünürlüğü/etkinliği, kapatma onay penceresi ve yeniden açma pop-up'ının açık/kapalı durumu.
 *
 * <p><b>Neden ayrı bir hook:</b> aynı karar iki ekrandan verilir — Toplantı Yönetimi (105) ve Onay
 * Bekleyenler'in Görev Kapatma sekmesi (131). 131 FR-016/FR-018 iki yolun <b>birebir aynı</b>
 * davranmasını ister; kural ikinci kez yazılsaydı bir sonraki düzeltme yalnız birine yapılırdı.
 *
 * @param confirmGroup kapatma onay penceresinin grubu — çağıran ekranın kendi
 *     {@code <ConfirmDialog group=…>} örneğiyle eşleşmelidir (iki ekran ayrı grup kullanır)
 */
export function useTaskDecisionActions(confirmGroup: string) {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  /**
   * Kısıtlı Yetkili asistan ekranı **salt okunur** görür (126 FR-019, 26.10.2026): Kapat / Yeniden
   * Aç ve onların seçim kutuları çizilmez. Sunucu da aynı kararı verir — satır bayrakları `false`
   * gelir, uç yazmayı reddeder; buradaki gizleme, çalışmayacak düğmeleri hiç göstermemek içindir.
   */
  const { readOnly } = useProxyMode()
  const [selection, setSelection] = useState<MeetingManagementTask[]>([])
  const [reopenOpen, setReopenOpen] = useState(false)
  const close = useCloseTasks()

  /*
   * Buton etkinliği: seçim boş DEĞİL **ve seçimdeki TÜM görevler** o işlem için uygun.
   *
   * "En az biri uygun" yetmez: uç "hepsi ya da hiçbiri" çalışır, dolayısıyla kısmi bir küme
   * gönderilirse işlem tümüyle reddedilir ve kullanıcı sebebi anlamaz. Kural burada, kullanıcı
   * butona basmadan ÖNCE uygulanır.
   */
  // Sunucunun toplu kapatma üst sınırı BURADA da uygulanır: sayfa boyutu 200'e çıkabiliyor ve
  // seçim sayfa değişiminde temizlenmiyor, yani 200'ü aşan bir seçim ULAŞILABİLİR. Sınır yalnız
  // sunucuda kalsaydı kullanıcı merkezî genel bir `400` görür, sebebini ekranda bulamazdı.
  const tooManyToClose = selection.length > CLOSE_MAX_ITEMS
  const canClose = selection.length > 0 && !tooManyToClose && selection.every((s) => s.closable)
  const canReopen = selection.length > 0 && selection.every((s) => s.reopenable)

  /**
   * Butonun **görünürlüğü** sayfadan bağımsızdır: ölçüt kullanıcının kapatma yetkisidir
   * (`MINUTES_CREATE` = Özel Kalem), sunucudaki `requirePrivateOffice` ile **aynı** kural.
   *
   * <p><b>Önceki hâli sayfaya bağlıydı</b> (`rows.some(r => r.closable)`) ve bu bir hataydı
   * (MR !220, Duo): `closable` hem durumu (331) hem yetkiyi taşıdığı için, hakkı OLAN bir kullanıcı
   * sırf o sayfada uygun satır bulunmadığında butonu **kaybediyordu**.
   *
   * <p><b>Etkinlik yine sunucudan gelir</b> (`canClose` → satır başına `closable`): istemci "durum
   * 331 mi" hesabını yeniden yapmaz.
   */
  const showCloseButton = !readOnly && hasGrant(GRANTS.MINUTES_CREATE)

  /**
   * Kapatma **onay ister** (kullanıcı isteği 2026-09-17). Metin **kaç görev** kapatılacağını yazar:
   * seçim sayfa değişiminde korunduğu için ekranda görünen satır sayısı ile seçimin boyutu aynı
   * olmayabilir.
   *
   * <p>Pencere `p-button-danger` KULLANMAZ: kapatma geri alınabilir bir karardır — "Yeniden Aç"
   * görevi yeni hedef teslim tarihiyle geri açar. Kırmızı onay, silmeyi bundan ayırt eden işarettir.
   */
  const onClose = () =>
    confirmDialog({
      group: confirmGroup,
      header: t('meetingManagement.close.confirmTitle'),
      message: t('meetingManagement.close.confirm', { count: selection.length }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('meetingManagement.action.close'),
      accept: () =>
        close.mutate(
          selection.map((s) => ({ id: s.id, modDate: s.modDate })),
          {
            onSuccess: () => {
              toast.success(t('meetingManagement.close.success'))
              setSelection([])
            },
            // Hata → merkezî bildirim (019); ekran kendi hata→mesaj eşlemesini tutmaz.
          },
        ),
    })

  /** Kart dalında tek satırın seçimini değiştirir (tabloda bunu PrimeReact yapar). */
  const toggleOne = (row: MeetingManagementTask, checked: boolean) =>
    setSelection((prev) => (checked ? [...prev, row] : prev.filter((s) => s.id !== row.id)))

  /**
   * Yeniden açma pop-up'ı kapanınca seçim sıfırlanır: pop-up yarıda kapatılsa bile işlenmiş görevler
   * kalıcıdır ve artık uygun olmayan satırlar seçili kalmamalıdır. Liste tazelemesi çağıranındır.
   */
  const closeReopen = () => {
    setReopenOpen(false)
    setSelection([])
  }

  return {
    readOnly,
    selection,
    setSelection,
    toggleOne,
    tooManyToClose,
    canClose,
    canReopen,
    showCloseButton,
    closePending: close.isPending,
    onClose,
    reopenOpen,
    openReopen: () => setReopenOpen(true),
    closeReopen,
  }
}

export type TaskDecisionActions = ReturnType<typeof useTaskDecisionActions>
